import React, { FC, useState, useContext, useEffect, useMemo, useRef } from 'react';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms/types';
import { MultiDropdownSendData, TDropdownContextSendData, TMultipleDropdownContextSendData } from './dropdownContext';
import ProductSelectorDropdowns from './components/productSelectorDropdowns';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import Icon from '../../../@shared/icon';
import { APIPrefix } from '../../../../constants/api.constants';
import { TPreProductSelectorResults, TProductSelector, TProductSelectorSelectedData } from '../../types/productSelector.types';
import useDidUpdate from '../../../../hooks/useDidUpdate.effect';
import { windowService } from '../../../../utils/window.utils';
import useFirstUserAction from '../../hooks/useFirstUserActionState';
import { CustomFormContext } from '../customFormContextProvider';
import useSitecoreContext from '../../../../hooks/useSitecoreContext';
import useCreateProductSelectorList from './hooks/useCreateProductSelectorList';
import useDefaultSelectedProduct from './hooks/useDefaultSelectedProduct';
import './styles.scss';
import  useGetLocalStorageValues from '../../../../hooks/useGetLocalStorageValues';
import { FORM_FIELDS_PRODUCT_SELECTOR } from '../../../../constants/formFields.constants';

const defaultSendData = {
  value: [
    {
      id: 0,
      line: '',
      series: '',
      models: '',
    },
  ],
};

const ProductSelector: FC<ValueFieldProps> = (props) => {
  const { field } = props;
  const id = field?.valueField?.id;
  const name = field?.valueField?.name;
  const FieldValidationErrors = props?.fieldValidationErrorsComponent;
  const isRequired = field?.model?.required;
  const [fetchedData, setFetchedData] = useState<TProductSelector[]>([]);
  const [preSelectedResults, setPreSelectedResults] = useState<TPreProductSelectorResults[]>([]);
  const [dropdowns, setDropdowns] = useState([{ id: 0, showDelete: false }]);
  const [value, setValue] = useState('');
  const countRef = useRef(0);
  const [validationErrors, setValidationErrors] = useState(false);
  const triggerFirstInteractionGTMEvent = useFirstUserAction();
  const productSectorList = useCreateProductSelectorList(fetchedData, preSelectedResults);
  const preSelectedProductData: TProductSelectorSelectedData | null = useDefaultSelectedProduct(preSelectedResults);
  const customFormContext = useContext(CustomFormContext);
  const localStorageValue = useGetLocalStorageValues(FORM_FIELDS_PRODUCT_SELECTOR);

  const generateUniqueId = () => {
    countRef.current += 1;
    return countRef.current;
  };

  const { sitecoreContext } = useSitecoreContext();

  const isEnabledPreSelection = customFormContext?.params?.enablePreselection === '1';
  const defaultSelectedLine = localStorageValue?.['product_line'] ||
    (!isEnabledPreSelection && sitecoreContext.analytics?.productLine) || preSelectedProductData?.defaultSelectedLine;
  const defaultSelectedSeries = localStorageValue?.['product_series'] ||
    (!isEnabledPreSelection && sitecoreContext.analytics?.productSeries) || preSelectedProductData?.defaultSelectedSeries;
  const defaultSelectedModel = localStorageValue?.['product_model'] ||
    (!isEnabledPreSelection && sitecoreContext.analytics?.productModel) || preSelectedProductData?.defaultSelectedModel;

  const preSelectionId = customFormContext?.params?.preSelection;

  const itemID = sitecoreContext?.route?.itemId;
  const siteLanguage = sitecoreContext?.language;

  const fetchData = (): void => {
    const getApiURL = `/${APIPrefix}/product/list/${itemID}/${siteLanguage}`;
    fetch(getApiURL)
      .then((res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw Error(`${res.status} : Failed to fetch data`);
        }
      })
      .then((result) => {
        setFetchedData(result?.lines);
      })
      .catch((error) => {
        console.error(`${error} occurred while fetching data`);
      });
  };

  const fetchProductPreselectedData = (): void => {
    const getApiURL = `/${APIPrefix}/productselector/list/${preSelectionId}/${siteLanguage}`;
    fetch(getApiURL)
      .then((res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw Error(`${res.status} : Failed to fetch product preselected list`);
        }
      })
      .then((result) => {
        setPreSelectedResults(result?.lines);
      })
      .catch((error) => {
        console.error(`${error} occurred while fetching data`);
      });
  };

  const [sendData, setSendData] = useState<TMultipleDropdownContextSendData>(() => defaultSendData);
  const decodeValue = (rawValue: string): string => windowService()?.atob(rawValue);

  useEffect(() => {
    if (!props.value && !validationErrors) {
      setDropdowns([{ id: 0, showDelete: false }]);
      if (defaultSelectedLine) {
        setSendData({
          value: [
            {
              id: 0,
              line: defaultSelectedLine,
              series: defaultSelectedSeries,
              models: defaultSelectedModel,
            },
          ],
        });
      } else {
        setSendData(defaultSendData);
      }
    } else if (props.value && !value) {
      const decodedValue = decodeValue(props.value);
      const itemValues = decodedValue.split(',');
      const dropdownValues = itemValues.map((value, index) => {
        const [id] = value.split('|');
        countRef.current = parseInt(id) || index;
        return { id: parseInt(id) || index, showDelete: index !== 0 };
      });
      const sendData = itemValues.map((value, index) => {
        const [id, selectedLine, selectedSeries, selectedModel] = value.split('|') || [];
        return {
          id: parseInt(id) || index,
          line: selectedLine,
          series: selectedSeries,
          models: selectedModel,
        };
      });
      setSendData({
        value: sendData,
      });
      setDropdowns(dropdownValues);
    }
  }, [props.value, defaultSelectedLine, defaultSelectedSeries, defaultSelectedModel]);

  useEffect(() => {
    fetchData();
    if (isEnabledPreSelection) fetchProductPreselectedData();
  }, []);

  const updateSendData = (id: number, newSendData: Partial<TDropdownContextSendData> = {}) => {
    setSendData((prevState) => ({
      ...prevState,
      value: prevState.value.map((item) => {
        if (item.id === id) {
          return { ...item, ...newSendData };
        }
        return item;
      }),
    }));
  };

  const handleAddButtonClick = () => {
    const newId = generateUniqueId();
    setDropdowns([...dropdowns, { id: newId, showDelete: true }]);
    setSendData((prevState) => ({
      ...prevState,
      value: [
        ...prevState.value,
        {
          id: newId,
          line: '',
          series: '',
          models: '',
        },
      ],
    }));
  };

  const handleDeleteButtonClick = (id) => {
    const updatedDropdowns = dropdowns.filter((dropdown) => dropdown.id !== id);
    setDropdowns(updatedDropdowns);
    const updateSendDataItems = {
      value: sendData.value.filter((item) => item.id !== id),
    };
    setSendData(updateSendDataItems);
  };

  const convertObjectToString = (obj) => {
    const objKeys = Object.keys(obj);
    return objKeys
      .filter((key) => {
        if (key == 'id' || key == 'line' || key == 'series' || key == 'models') {
          return key;
        }
      })
      .map((key) => obj[key])
      .join('|');
  };

  const sendProductSelection = sendData.value.map((obj) => convertObjectToString(obj)).join(',');

  const encodeValue = (rawValue: string): string => windowService()?.btoa(rawValue);

  useDidUpdate(() => {
    const hasEmptyLineValues = sendData.value.some((obj) => obj.line?.trim() === '');
    if (isRequired && hasEmptyLineValues) {
      setValidationErrors(true);
    } else {
      setValidationErrors(false);
    }
    const sendValue = isRequired && hasEmptyLineValues ? '' : encodeValue(sendProductSelection);

    setValue(sendProductSelection);
    customFormContext?.setProductSelector(sendData.value);
    props.onChange(field?.valueField?.name, sendValue, props?.isValid, []);
    triggerFirstInteractionGTMEvent();
  }, [sendData]);

  const sendContext = useMemo(
    () => ({
      ...sendData,
      updateSendData,
      isLineHidden: preSelectedProductData?.isLineHidden,
      isSeriesHidden: preSelectedProductData?.isSeriesHidden,
    }),
    [sendData]
  );

  return (
    <div className="sitecore-jss-form__control--product-dropdown-wrapper">
      <input id={id} name={name} className="sitecore-jss-form__field" type="hidden" value={value} form="novalidatedform" />
      {dropdowns.map((dropdown) => (
        <MultiDropdownSendData.Provider value={sendContext} key={dropdown.id}>
          <div
            key={dropdown.id}
            className={`sitecore-jss-form__control--product-dropdown-list ${!dropdown?.showDelete ? 'sitecore-jss-form__control--product-dropdown-list--empty' : ''
              }`}
          >
            <ProductSelectorDropdowns
              id={dropdown.id}
              fields={props}
              optionsList={productSectorList}
              isValidationErrors={validationErrors}
              defaultSelectedLine={defaultSelectedLine}
              defaultSelectedSeries={defaultSelectedSeries}
              defaultSelectedModel={defaultSelectedModel}
            />
            {dropdown.showDelete && (
              <Icon
                iconName={'action-delete-2'}
                size={24}
                extraClassName="form-custom-dropdown__delete-icon"
                onClick={() => handleDeleteButtonClick(dropdown.id)}
              />
            )}
          </div>
        </MultiDropdownSendData.Provider>
      ))}
      <div
        onClick={handleAddButtonClick}
        className={`form-custom-dropdown__add ${customFormContext?.params?.productLimit === (sendData.value.length - 1).toString() ? 'form-custom-dropdown__add--disable' : ''
          }`}
      >
        <p>
          <DictionaryTerm keyword={'cnhi-forms-fields-product-selector-add'} />
        </p>
        <Icon iconName={'action-plus'} size={24} extraClassName="form-custom-dropdown__plus-icon" color="primary" />
      </div>
      {FieldValidationErrors && <FieldValidationErrors {...props} />}
    </div>
  );
};

export default ProductSelector;
